import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  HardwareModel,
  OrgEntityType,
  SecureTenantConfig,
  SecureTenantConfigResponse,
} from '@shared/interfaces/tenant';
import { environment } from 'environments/environment';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SecureTenantConfigService {
  private hasLoaded = false;
  private secureTenantConfigCore: SecureTenantConfig = {};

  get merchantFacilityAllowedCountries(): string[] {
    return this.secureTenantConfigCore.merchant_facility_allowed_countries ?? [];
  }

  get mobileNumberAllowedCountries(): string[] {
    return this.secureTenantConfigCore.mobile_number_allowed_countries ?? [];
  }

  get hardwareModels(): HardwareModel[] {
    return this.secureTenantConfigCore.hardware_models ?? [];
  }

  get orgEntityTypes(): OrgEntityType[] {
    return this.secureTenantConfigCore.org_entity_types ?? [];
  }

  constructor(private http: HttpClient) {}

  /**
   * True only when every registration method is VIA_CODE; an empty list is not code-only.
   * Accepts either the tenant config id or the display name, since callers hold one or the other
   * depending on where the model came from.
   */
  isCodeOnlyRegistrationModel(idOrDisplayName: string | null | undefined): boolean {
    const model = this.hardwareModels.find(
      (candidate) => candidate.id === idOrDisplayName || candidate.display_name === idOrDisplayName,
    );
    const methods = model?.supported_registration_methods ?? [];
    return methods.length > 0 && methods.every((method) => method === 'VIA_CODE');
  }

  loadSecureTenantConfig(): Observable<boolean> {
    if (this.hasLoaded) {
      return of(true);
    }

    const url = `${environment.api_base}tenant-config`;
    const headers = new HttpHeaders({ 'global-error-handler': 'false' });

    return this.http.get<SecureTenantConfigResponse>(url, { headers }).pipe(
      tap(({ data }) => {
        this.secureTenantConfigCore = data;
        this.hasLoaded = true;
      }),
      map(() => true),
      catchError(() => {
        return of(false);
      }),
    );
  }
}
